import {
  AirHandlerTypeCodeType,
  ConcealedDuctLocationType,
  ConfigurationType,
  GridSpaceType,
  LevelType,
  LineSetPointType,
  LineSetType
} from 'data/types';
import { Vector3 } from 'three';
import {
  calculateLevelYOffsets,
  getLevelHeight,
  getLevelIndexForSpace,
  SCALE
} from 'utils/3d';
import {
  EQUIPMENT_GRID_SQUARES,
  getAirHandler3DPlacement,
  getCondenser3DPlacement
} from 'utils/3d-equipment';
import {
  getConcealedDuctUnitWorldY,
  resolveConcealedDuctLocation
} from 'utils/concealed-duct-placement';
import { getEffectiveFootprintPolygons } from 'utils/footprint-polygons';
import { LinesetPerimetersByLevelType } from 'utils/lineset-level-perimeters-types';
import { resolveLinesetPointLevels } from 'utils/lineset-point-levels';
import { snapLinesetPoint } from 'utils/lineset-snap';

export const LINESET_SPACE_FLOOR_HEIGHT = 2.5;
export const LINESET_FIRST_HALF_WALL_OFFSET = 0.25;
export const LINESET_ATTIC_RUN_HEIGHT = 0.25;
export const LINESET_CONDENSER_GRID_SQUARES = 3;
export const LINESET_CONDENSER_WIDTH = 1.25;
export const LINESET_CONDENSER_DEPTH = 1.25;
export const LINESET_AIR_HANDLER_OFFSET = 0.55;

export type LineSetHalf = 'condenser' | 'airHandler';

export type CondenserFootprint = {
  uniqueId: string;
  centerX: number;
  centerZ: number;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  placementType: 'ground' | 'wall' | 'roof';
  position: { x: number; y: number };
  heightOffGround: number | undefined;
};

export type AirHandlerFootprint = {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  position: { x: number; y: number };
  airHandlerTypeCode: AirHandlerTypeCodeType | undefined;
  concealedLocation: ConcealedDuctLocationType | null;
};

export type LineSetPointGeometry = {
  point: LineSetPointType;
  index: number;
  x: number;
  y: number;
  z: number;
  levelBaseY: number;
  derivedY: number;
  half: LineSetHalf;
  levelIndex: number;
  minHeightFeet: number;
  maxHeightFeet: number;
  heightLocked: boolean;
  overlappingCondenser: CondenserFootprint | undefined;
  overlappingAirHandler: boolean;
};

export type LineSetSegmentGeometry = {
  index: number;
  half: LineSetHalf;
  startX: number;
  startY: number;
  startZ: number;
  endX: number;
  endY: number;
  endZ: number;
  riserFromY?: number;
};

export type LinesetLevelRange = {
  levelUniqueId: string;
  levelIndex: number;
  baseY: number;
  topY: number;
};

export type LineSetGeometry = {
  points: LineSetPointGeometry[];
  segments: LineSetSegmentGeometry[];
  isCrossFloor: boolean;
  transitionPointIndex: number;
  condenserLevelIndex: number;
  airHandlerLevelIndex: number;
  levelRanges: LinesetLevelRange[];
};

export function getAirHandlerRunHeight(
  typeCode: AirHandlerTypeCodeType | undefined
): number {
  if (!typeCode) return 2.0;
  if (typeCode === 'heatingConsole') return 0.5;
  if (typeCode === 'console') return 0.625;
  if (
    typeCode === 'concealed' ||
    typeCode === 'concealed_high_static' ||
    typeCode === 'cassette' ||
    typeCode === 'cassette_16' ||
    typeCode === 'cassette_drop_ceiling'
  ) {
    return 2.4;
  }
  return 2.0;
}

export function resolveLineSetAirHandlerTypeCode(
  lineset: LineSetType,
  configuration: ConfigurationType | undefined,
  fallback?: AirHandlerTypeCodeType
): AirHandlerTypeCodeType | undefined {
  if (lineset.airHandlerTypeCode) return lineset.airHandlerTypeCode;
  if (fallback) return fallback;
  if (!configuration) return undefined;

  for (const condenser of configuration.condensers) {
    for (const zone of condenser.zones) {
      if (
        zone.airHandler?.uniqueId === lineset.airHandlerUniqueId ||
        zone.zoneItems.some(
          (item) => item.uniqueId === lineset.airHandlerUniqueId
        )
      ) {
        return zone.airHandler?.airHandlerTypeCode;
      }
    }
  }
  return undefined;
}

export function getCondenserFootprints(
  configuration: ConfigurationType | undefined
): CondenserFootprint[] {
  if (!configuration) return [];

  return configuration.condensers
    .filter((condenser) => condenser.position)
    .map((condenser) => {
      const centerX =
        (condenser.position!.x + LINESET_CONDENSER_GRID_SQUARES / 2) * SCALE;
      const centerZ =
        (condenser.position!.y + LINESET_CONDENSER_GRID_SQUARES / 2) * SCALE;
      return {
        uniqueId: condenser.uniqueId,
        centerX,
        centerZ,
        minX: centerX - LINESET_CONDENSER_WIDTH / 2,
        maxX: centerX + LINESET_CONDENSER_WIDTH / 2,
        minZ: centerZ - LINESET_CONDENSER_DEPTH / 2,
        maxZ: centerZ + LINESET_CONDENSER_DEPTH / 2,
        placementType: condenser.placementType ?? 'roof',
        position: { x: condenser.position!.x, y: condenser.position!.y },
        heightOffGround: condenser.heightOffGround ?? undefined
      };
    });
}

export function getLineSetAirHandlerFootprint(
  lineset: LineSetType,
  configuration: ConfigurationType | undefined,
  fallbackTypeCode?: AirHandlerTypeCodeType
): AirHandlerFootprint | undefined {
  if (!configuration) return undefined;

  for (const condenser of configuration.condensers) {
    for (const zone of condenser.zones) {
      const matches =
        zone.airHandler?.uniqueId === lineset.airHandlerUniqueId ||
        zone.zoneItems.some(
          (item) => item.uniqueId === lineset.airHandlerUniqueId
        );
      if (matches && zone.airHandler) {
        const { positionX, positionY, airHandlerTypeCode } = zone.airHandler;
        const minX = positionX * SCALE;
        const minZ = positionY * SCALE;
        const size = EQUIPMENT_GRID_SQUARES * SCALE;
        return {
          minX,
          maxX: minX + size,
          minZ,
          maxZ: minZ + size,
          position: { x: positionX, y: positionY },
          airHandlerTypeCode: airHandlerTypeCode ?? fallbackTypeCode,
          concealedLocation: zone.airHandler.concealedLocation ?? null
        };
      }
    }
  }
  return undefined;
}

export function findOverlappingAirHandler(
  point: LineSetPointType,
  footprint: AirHandlerFootprint | undefined
): boolean {
  if (!footprint) return false;
  const pointX = point.positionX * SCALE;
  const pointZ = point.positionY * SCALE;
  return (
    pointX >= footprint.minX &&
    pointX <= footprint.maxX &&
    pointZ >= footprint.minZ &&
    pointZ <= footprint.maxZ
  );
}

export function findOverlappingCondenser(
  point: LineSetPointType,
  footprints: CondenserFootprint[]
): CondenserFootprint | undefined {
  const pointX = point.positionX * SCALE;
  const pointZ = point.positionY * SCALE;

  return footprints.find(
    (footprint) =>
      pointX >= footprint.minX &&
      pointX <= footprint.maxX &&
      pointZ >= footprint.minZ &&
      pointZ <= footprint.maxZ
  );
}

export function getTransitionPointIndex(lineset: LineSetType): number {
  if (lineset.isCrossFloor !== true) return -1;
  return Math.max(lineset.points.length - 2, 1);
}

export function getSegmentHalf(
  segmentIndex: number,
  transitionPointIndex: number
): LineSetHalf {
  if (transitionPointIndex >= 0 && segmentIndex < transitionPointIndex) {
    return 'condenser';
  }
  return 'airHandler';
}

export function getRunHeightWorld(
  half: LineSetHalf,
  airHandlerTypeCode: AirHandlerTypeCodeType | undefined
): number {
  if (half === 'condenser') {
    return LINESET_SPACE_FLOOR_HEIGHT - LINESET_FIRST_HALF_WALL_OFFSET;
  }
  return getAirHandlerRunHeight(airHandlerTypeCode);
}

export function getRunHeightFeet(
  half: LineSetHalf,
  airHandlerTypeCode: AirHandlerTypeCodeType | undefined
): number {
  return getRunHeightWorld(half, airHandlerTypeCode) / SCALE;
}

export function getPointHeightFeet(
  point: LineSetPointType,
  half: LineSetHalf,
  airHandlerTypeCode: AirHandlerTypeCodeType | undefined
): number {
  if (point.positionZ !== null && point.positionZ !== undefined) {
    return point.positionZ;
  }
  return getRunHeightFeet(half, airHandlerTypeCode);
}

type ComputeLineSetGeometryArgs = {
  lineset: LineSetType;
  configuration: ConfigurationType | undefined;
  structure: LevelType[];
  yOffset: number;
  airHandlerTypeCode?: AirHandlerTypeCodeType;
  spaces?: GridSpaceType[];
};

export function computeLineSetGeometry({
  lineset,
  configuration,
  structure,
  yOffset,
  airHandlerTypeCode,
  spaces = []
}: ComputeLineSetGeometryArgs): LineSetGeometry {
  const levelYOffsets = calculateLevelYOffsets(structure);

  const getLevelYOffset = (idx: number): number =>
    levelYOffsets[idx]?.yOffset ?? 0;

  const getLevelIndexById = (levelUniqueId: string | undefined): number => {
    if (!levelUniqueId) return 0;
    const idx = structure.findIndex((l) => l.uniqueId === levelUniqueId);
    return Math.max(idx, 0);
  };

  const condenserLevelIndex = getLevelIndexById(lineset.condenserLevelUniqueId);
  const condenserLevelSpaces = spaces.filter(
    (space) =>
      getLevelIndexForSpace(space.id, structure) === condenserLevelIndex
  );
  const condenserLevelFootprints = getEffectiveFootprintPolygons(
    structure[condenserLevelIndex]
  );
  const airHandlerLevelIndex = getLevelIndexById(
    lineset.airHandlerLevelUniqueId
  );
  const isCrossFloor = lineset.isCrossFloor === true;
  const transitionPointIndex = getTransitionPointIndex(lineset);

  const pointLevels = resolveLinesetPointLevels(lineset);
  const distinctPointLevels = new Set(
    pointLevels.filter((levelUniqueId): levelUniqueId is string =>
      Boolean(levelUniqueId)
    )
  );
  const isMultiLevel = isCrossFloor || distinctPointLevels.size > 1;

  const resolvedTypeCode = resolveLineSetAirHandlerTypeCode(
    lineset,
    configuration,
    airHandlerTypeCode
  );

  const footprints = getCondenserFootprints(configuration);
  const airHandlerFootprint = getLineSetAirHandlerFootprint(
    lineset,
    configuration,
    resolvedTypeCode
  );
  const airHandlerConcealedLocation = airHandlerFootprint
    ? resolveConcealedDuctLocation(
        airHandlerFootprint.concealedLocation,
        airHandlerLevelIndex,
        structure
      )
    : null;
  const airHandlerRunHeightWorld = airHandlerConcealedLocation
    ? getConcealedDuctUnitWorldY(airHandlerConcealedLocation)
    : getAirHandlerRunHeight(resolvedTypeCode);
  const lastIndex = lineset.points.length - 1;

  const getLevelIndexForHalf = (half: LineSetHalf): number => {
    if (!isCrossFloor) return condenserLevelIndex;
    return half === 'condenser' ? condenserLevelIndex : airHandlerLevelIndex;
  };

  const getLevelIndexForPoint = (index: number, half: LineSetHalf): number => {
    const levelUniqueId = pointLevels[index];
    if (levelUniqueId) return getLevelIndexById(levelUniqueId);
    return getLevelIndexForHalf(half);
  };

  const getLevelBaseYForPoint = (index: number, half: LineSetHalf): number => {
    if (!isMultiLevel) {
      return yOffset;
    }
    return yOffset + getLevelYOffset(getLevelIndexForPoint(index, half));
  };

  const getDerivedYForPoint = (index: number, half: LineSetHalf): number => {
    const baseY = getLevelBaseYForPoint(index, half);
    const levelUniqueId = pointLevels[index];
    if (isMultiLevel && levelUniqueId) {
      const level = structure[getLevelIndexById(levelUniqueId)];
      if (level?.type === 'attic') {
        return baseY + LINESET_ATTIC_RUN_HEIGHT;
      }
      if (levelUniqueId === lineset.airHandlerLevelUniqueId) {
        return baseY + airHandlerRunHeightWorld;
      }
      return (
        baseY + LINESET_SPACE_FLOOR_HEIGHT - LINESET_FIRST_HALF_WALL_OFFSET
      );
    }
    if (half === 'airHandler') {
      return baseY + airHandlerRunHeightWorld;
    }
    return baseY + getRunHeightWorld(half, resolvedTypeCode);
  };

  const getEffectiveY = (
    point: LineSetPointType,
    index: number,
    half: LineSetHalf
  ): number => {
    if (point.positionZ !== null && point.positionZ !== undefined) {
      return getLevelBaseYForPoint(index, half) + point.positionZ * SCALE;
    }
    return getDerivedYForPoint(index, half);
  };

  const condenserBaseOffset = isMultiLevel
    ? yOffset + getLevelYOffset(condenserLevelIndex)
    : yOffset;
  const airHandlerBaseOffset = isMultiLevel
    ? yOffset + getLevelYOffset(airHandlerLevelIndex)
    : yOffset;

  const points: LineSetPointGeometry[] = lineset.points.map((point, index) => {
    const half = getSegmentHalf(index, transitionPointIndex);
    const isEndpoint = index === 0 || index === lastIndex;
    const isTransitionPoint = isCrossFloor && index === transitionPointIndex;
    const overlappingCondenser = isEndpoint
      ? findOverlappingCondenser(point, footprints)
      : undefined;
    const overlappingAirHandler =
      isEndpoint &&
      !overlappingCondenser &&
      findOverlappingAirHandler(point, airHandlerFootprint);
    const levelIndex = getLevelIndexForPoint(index, half);
    const levelType = structure[levelIndex]?.type ?? 'level';

    let x = point.positionX * SCALE;
    let z = point.positionY * SCALE;
    let y = getEffectiveY(point, index, half);

    const nextPoint = lineset.points[index + 1];
    const isRiserAnchor =
      isMultiLevel &&
      !isEndpoint &&
      nextPoint !== undefined &&
      pointLevels[index] !== undefined &&
      pointLevels[index + 1] !== undefined &&
      pointLevels[index] !== pointLevels[index + 1] &&
      (point.positionX !== nextPoint.positionX ||
        point.positionY !== nextPoint.positionY);

    if (isRiserAnchor) {
      y = Math.max(y, getEffectiveY(nextPoint, index + 1, half));
    }

    if (overlappingCondenser) {
      const placement = getCondenser3DPlacement(
        {
          position: overlappingCondenser.position,
          placementType: overlappingCondenser.placementType,
          heightOffGround: overlappingCondenser.heightOffGround
        },
        condenserLevelSpaces,
        condenserLevelFootprints
      );
      x = placement.x;
      z = placement.z;
      y = condenserBaseOffset + placement.y;
    } else if (overlappingAirHandler && airHandlerFootprint) {
      const placement = getAirHandler3DPlacement(
        {
          position: airHandlerFootprint.position,
          airHandlerTypeCode: airHandlerFootprint.airHandlerTypeCode,
          concealedLocation: airHandlerConcealedLocation
        },
        spaces
      );
      x = placement.x;
      z = placement.z;
      y = airHandlerBaseOffset + placement.y;
    }

    return {
      point,
      index,
      x,
      y,
      z,
      levelBaseY: getLevelBaseYForPoint(index, half),
      derivedY: getDerivedYForPoint(index, half),
      half,
      levelIndex,
      minHeightFeet: 0,
      maxHeightFeet: getLevelHeight(levelType) / SCALE,
      heightLocked:
        isEndpoint ||
        isTransitionPoint ||
        isRiserAnchor ||
        overlappingCondenser !== undefined,
      overlappingCondenser,
      overlappingAirHandler
    };
  });

  const segments: LineSetSegmentGeometry[] = [];
  for (let index = 0; index < lineset.points.length - 1; index += 1) {
    const half = getSegmentHalf(index, transitionPointIndex);
    const startPoint = lineset.points[index];
    const endPoint = lineset.points[index + 1];

    const startY = getEffectiveY(startPoint, index, half);
    const endY = getEffectiveY(endPoint, index + 1, half);

    const startLevelUniqueId = pointLevels[index];
    const endLevelUniqueId = pointLevels[index + 1];
    const movesHorizontally =
      startPoint.positionX !== endPoint.positionX ||
      startPoint.positionY !== endPoint.positionY;
    const isLevelTransition =
      isMultiLevel &&
      startLevelUniqueId !== undefined &&
      endLevelUniqueId !== undefined &&
      startLevelUniqueId !== endLevelUniqueId &&
      movesHorizontally &&
      Math.abs(startY - endY) > 0.001;

    segments.push({
      index,
      half,
      startX: startPoint.positionX * SCALE,
      startY: isLevelTransition ? endY : startY,
      startZ: startPoint.positionY * SCALE,
      endX: endPoint.positionX * SCALE,
      endY,
      endZ: endPoint.positionY * SCALE,
      ...(isLevelTransition ? { riserFromY: startY } : {})
    });
  }

  const levelRanges: LinesetLevelRange[] = structure.map((level, index) => {
    const baseY = isMultiLevel
      ? yOffset + getLevelYOffset(index)
      : yOffset + getLevelYOffset(index) - getLevelYOffset(condenserLevelIndex);
    return {
      levelUniqueId: level.uniqueId,
      levelIndex: index,
      baseY,
      topY: baseY + getLevelHeight(level.type)
    };
  });

  return {
    points,
    segments,
    isCrossFloor,
    transitionPointIndex,
    condenserLevelIndex,
    airHandlerLevelIndex,
    levelRanges
  };
}

export type LinesetPointDragUpdate = {
  positionX: number;
  positionY: number;
  positionZ?: number;
  levelUniqueId?: string;
};

const DRAG_HEIGHT_EPSILON = 0.001;
const DRAG_POSITION_EPSILON = 1e-6;
const DRAG_HEIGHT_SNAP = SCALE / 2;

function pickLevelRangeForY(
  levelRanges: LinesetLevelRange[],
  y: number,
  currentLevelIndex: number
): LinesetLevelRange | undefined {
  const current = levelRanges[currentLevelIndex];
  if (
    current &&
    y >= current.baseY - DRAG_HEIGHT_EPSILON &&
    y <= current.topY + DRAG_HEIGHT_EPSILON
  ) {
    return current;
  }
  for (const range of levelRanges) {
    if (
      y >= range.baseY - DRAG_HEIGHT_EPSILON &&
      y <= range.topY + DRAG_HEIGHT_EPSILON
    ) {
      return range;
    }
  }
  return current;
}

export function resolveLinesetPointDrag(
  pointGeometry: LineSetPointGeometry,
  anchorPosition: Vector3,
  levelRanges: LinesetLevelRange[] = [],
  perimetersByLevel: LinesetPerimetersByLevelType = new Map()
): LinesetPointDragUpdate {
  let snappedY;
  let targetRange: LinesetLevelRange | undefined;
  if (pointGeometry.heightLocked) {
    snappedY = pointGeometry.y;
  } else {
    snappedY = anchorPosition.y;
    if (Math.abs(snappedY - pointGeometry.derivedY) >= DRAG_HEIGHT_EPSILON) {
      snappedY = Math.round(snappedY / DRAG_HEIGHT_SNAP) * DRAG_HEIGHT_SNAP;
    }

    if (levelRanges.length > 0) {
      const minY = levelRanges.reduce(
        (min, range) => Math.min(min, range.baseY),
        Infinity
      );
      const maxY = levelRanges.reduce(
        (max, range) => Math.max(max, range.topY),
        -Infinity
      );
      snappedY = Math.min(Math.max(snappedY, minY), maxY);
      targetRange = pickLevelRangeForY(
        levelRanges,
        snappedY,
        pointGeometry.levelIndex
      );
    } else {
      const minY =
        pointGeometry.levelBaseY + pointGeometry.minHeightFeet * SCALE;
      const maxY =
        pointGeometry.levelBaseY + pointGeometry.maxHeightFeet * SCALE;
      snappedY = Math.min(Math.max(snappedY, minY), maxY);
    }
  }

  const dragLevelUniqueId =
    targetRange?.levelUniqueId ??
    levelRanges[pointGeometry.levelIndex]?.levelUniqueId;
  const snappedGrid = snapLinesetPoint(
    { x: anchorPosition.x / SCALE, y: anchorPosition.z / SCALE },
    (dragLevelUniqueId && perimetersByLevel.get(dragLevelUniqueId)) || []
  );
  anchorPosition.set(snappedGrid.x * SCALE, snappedY, snappedGrid.y * SCALE);

  const update: LinesetPointDragUpdate = {
    positionX: snappedGrid.x,
    positionY: snappedGrid.y
  };

  if (!pointGeometry.heightLocked) {
    const currentRange = levelRanges[pointGeometry.levelIndex];
    const changedLevel =
      targetRange !== undefined &&
      targetRange.levelUniqueId !== currentRange?.levelUniqueId;

    if (targetRange) {
      update.levelUniqueId = targetRange.levelUniqueId;
    }

    if (changedLevel && targetRange) {
      update.positionZ = Math.max(
        0,
        Math.round(((snappedY - targetRange.baseY) / SCALE) * 2) / 2
      );
    } else {
      const wasCustom =
        pointGeometry.point.positionZ !== null &&
        pointGeometry.point.positionZ !== undefined;
      const matchesDerived =
        Math.abs(snappedY - pointGeometry.derivedY) < DRAG_HEIGHT_EPSILON;
      if (wasCustom || !matchesDerived) {
        update.positionZ =
          Math.round(((snappedY - pointGeometry.levelBaseY) / SCALE) * 2) / 2;
      }
    }
  }

  return update;
}

export function isLinesetPointDragUnchanged(
  startPoint: LineSetPointType,
  update: LinesetPointDragUpdate,
  startLevelUniqueId?: string
): boolean {
  const startZ = startPoint.positionZ ?? undefined;
  const startLevel = startPoint.levelUniqueId ?? startLevelUniqueId;
  const levelUnchanged =
    update.levelUniqueId === undefined || update.levelUniqueId === startLevel;
  return (
    levelUnchanged &&
    Math.abs(update.positionX - startPoint.positionX) < DRAG_POSITION_EPSILON &&
    Math.abs(update.positionY - startPoint.positionY) < DRAG_POSITION_EPSILON &&
    update.positionZ === startZ
  );
}
